import React from 'react';
import { doChipStore } from '../store/doChipStore';
import { freeBetStore, gameConfigStore, notificationStore, userInfoStore } from '@pascal/pascal-core/shared/core/store';
import { betResponseCodeByName } from '../enums/betResponseCode';
import { gameStore } from '../store/gameStore';
import { BetType } from '../enums/tableNumbers';
import { autoBetStore } from '../store/autoBetStore';
import {iframeService, parseNumberByPrecision, platformMessageStore} from '@pascal/pascal-core';
import { marketType } from '../enums/marketType';
import { allChipsTotal } from '../helpers/chipsTotal';
import { getKeyByValue } from '../helpers/getKeyByValue';
import { soundService, soundTypes } from '../services/SoundService';
import { winningAmountStore } from '../store/winningAmountStore';
import { mathConfig, mathJS } from '../helpers/mathFormat';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';
import {PlatformErrorCodes} from "../enums/platformEnums";

export const useRouletteDoBetValidation = () => {
  const {
    setDoBet,
    setDoubleBet,
    setDoBetInCombination,
    getAllBetsModels: allBetsModels,
    selectedChip,
    limitsByBetType,
  } = doChipStore;
  const { balance } = userInfoStore;
  const { setLastWin } = winningAmountStore;
  const { activeFreeBet } = freeBetStore;

  const checkValidChip = (nextChipModel, isDouble, betType) => {
    const type = nextChipModel.chipType;

    const prevCurrentChipsAllAmount =
      allBetsModels.length &&
      allBetsModels[0]?.betModel?.reduce(
        (acc, cur) => {
          if (nextChipModel?.betType && getKeyByValue(marketType, cur.bet.type).includes(nextChipModel?.betType)) {
            cur.includedBets.forEach((item) => {
              if (type === getKeyByValue(marketType, item.type)) {
                acc.currentChipTotal = +mathJS.format(item.amount + acc.currentChipTotal, mathConfig);
              }
            });
          }
          if (cur.includedBets) {
            cur.includedBets.forEach((item) => {
              if (type === getKeyByValue(marketType, item.type)) {
                acc.allChipTotal = +mathJS.format(item.amount + acc.allChipTotal, mathConfig);
              }
            });
          } else {
            if (type === getKeyByValue(marketType, cur.bet.type)) {
              acc.allChipTotal = +mathJS.format(acc.allChipTotal + cur.bet.amount, mathConfig);
            }
            if (!nextChipModel?.betType && type === getKeyByValue(marketType, cur.bet.type)) {
              acc.currentChipTotal = +mathJS.format(acc.currentChipTotal + cur.bet.amount, mathConfig);
            }
          }
          return acc;
        },
        {
          allChipTotal: 0,
          currentChipTotal: 0,
        }
      );

    const maxLimitKey = Object.keys(limitsByBetType).find((item) => {
      if (item.includes('/')) {
        const searchedValue = item.split('/');
        return searchedValue.some((item) => type.toLowerCase().match(item.toLowerCase()));
      }
      return type.toLowerCase().match(item.toLowerCase());
    });

    const maxLimit = limitsByBetType[maxLimitKey].maxBet;
    const minLimit = limitsByBetType[maxLimitKey].minBet;

    if (isDouble) {
      if (prevCurrentChipsAllAmount.allChipTotal * 2 > maxLimit) {
        return false;
      } else {
        return {
          [type]: +mathJS.format(prevCurrentChipsAllAmount.currentChipTotal * 2, mathConfig),
          type,
          betType,
        };
      }
    }

    if (nextChipModel[type] < minLimit) {
      return;
    }
    if (!prevCurrentChipsAllAmount.allChipTotal) {
      if (nextChipModel[type] > maxLimit) {
        return false;
      }
      return {
        [type]: nextChipModel[type],
        type,
      };
    } else {
      if (nextChipModel[type] + prevCurrentChipsAllAmount.allChipTotal > maxLimit) {
        return false;
      }
      return {
        [type]: parseNumberByPrecision(
          1,
          +mathJS.format(+(nextChipModel[type] + prevCurrentChipsAllAmount.currentChipTotal), mathConfig)
        ),
        type,
      };
    }
  };

  const handleDoBetWithValid = (nextChipModel, isFavoriteBet) => {
    if (gameStore.isBetsClosed) {
      return;
    }
    if (
      activeFreeBet.id &&
      allBetsModels[0]?.betModel &&
      activeFreeBet.remainingCount - allBetsModels[0]?.betModel.length === 0
    ) {
      notificationStore.setNotificationType(betResponseCodeByName.NotFreeChip);
      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }

    if (autoBetStore.autoBetModel) {
      autoBetStore.resetAutoBet();
    }

    setLastWin(0);
    const isValidResult = checkValidChip(nextChipModel);
    const total = allChipsTotal(allBetsModels[0]);
    if (isFavoriteBet) {
      return isValidResult;
    }

    if ((total || 0) + selectedChip > balance && !activeFreeBet.id) {
      !platformMessageStore.isShowAllNotification ? iframeService.sendErrorReceived(
          {"errorCode": PlatformErrorCodes.NotEnoughMoney}
      ) : notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance)

      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }
    if (!isValidResult) {
      notificationStore.setNotificationType(betResponseCodeByName.InValidMaxBet);
      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }
    userSettingsStore.isSoundOn && soundService.play(soundTypes.chip);
    setDoBet(isValidResult);
  };

  const handleDoubleBetWithValid = () => {
    if (gameStore.isBetsClosed) {
      return;
    }

    if (autoBetStore.autoBetModel) {
      autoBetStore.resetAutoBet();
    }

    setLastWin(0);
    const isValidResult = allBetsModels[0]?.betModel?.reduce((acc, cur) => {
      if (cur.includedBets) {
        cur.includedBets.forEach((item) => {
          const chipType = getKeyByValue(marketType, item.type);
          const betType = getKeyByValue(marketType, cur.bet.type);
          acc.push({
            ...checkValidChip({ ...item, [chipType]: item.amount, chipType, betType }, true),
            betType,
          });
        });
      } else {
        const chipType = getKeyByValue(marketType, cur.bet.type);
        acc.push({
          ...checkValidChip({ ...cur.bet, [chipType]: cur.bet.amount, chipType }, true),
        });
      }
      return acc;
    }, []);

    if (isValidResult.some((item) => !item[item.type])) {
      notificationStore.setNotificationType(betResponseCodeByName.InValidMaxBet);
      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }

    const result = isValidResult.reduce(
      (acc, cur) => {
        if (cur) {
          const { type, betType } = cur;
          acc.combinations.push({
            [type]: cur[type],
            type,
            betType,
          });
        }
        return acc;
      },
      {
        combinations: [],
      }
    );

    const total = result.combinations.reduce((acc, cur) => {
      acc = +mathJS.format(acc + cur[cur.type], mathConfig);
      return acc;
    }, 0);

    if (total > balance) {
      !platformMessageStore.isShowAllNotification ? iframeService.sendErrorReceived(
          {"errorCode": PlatformErrorCodes.NotEnoughMoney}
      ) : notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance)

      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }
    userSettingsStore.isSoundOn && soundService.play(soundTypes.chip);
    setDoubleBet(result);
  };

  const handleDoBetInCombination = (combination, chip, betType, isFavoriteBet) => {
    if (gameStore.isBetsClosed) {
      return;
    }

    if (
      activeFreeBet.id &&
      allBetsModels[0]?.betModel &&
      activeFreeBet.remainingCount - allBetsModels[0]?.betModel.length === 0
    ) {
      notificationStore.setNotificationType(betResponseCodeByName.NotFreeChip);
      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }

    const total = allChipsTotal(allBetsModels[0]);
    if (autoBetStore.autoBetModel) {
      autoBetStore.resetAutoBet();
    }

    setLastWin(0);
    const isValidResults = combination.reduce(
      (acc, cur) => {
        const validResult = checkValidChip(
          {
            [cur]:
              betType === BetType.Voisins && (cur === BetType.Street_0_2_3 || cur === BetType.Corner_25_26_28_29)
                ? +mathJS.format(chip * 2, mathConfig)
                : chip,
            betType,
            chipType: cur,
          },
          false,
          betType
        );
        acc.result.push({ ...validResult, betType });
        return acc;
      },
      {
        result: [],
        total: 0,
      }
    );

    if (isFavoriteBet) {
      return isValidResults.result;
    }

    if ((total || 0) + combination.length * selectedChip > balance && !activeFreeBet.id) {
      !platformMessageStore.isShowAllNotification ? iframeService.sendErrorReceived(
          {"errorCode": PlatformErrorCodes.NotEnoughMoney}
      ) : notificationStore.setNotificationType(betResponseCodeByName.NotEnoughBalance)

      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }
    if (isValidResults.result.some((item) => !item[item.type])) {
      notificationStore.setNotificationType(betResponseCodeByName.InValidMaxBet);
      setTimeout(() => {
        notificationStore.setNotificationType(null);
      }, 1000);
      return;
    }

    if (activeFreeBet && isValidResults.result?.length > activeFreeBet.remainingCount) {
      return;
    }
    userSettingsStore.isSoundOn && soundService.play(soundTypes.chip);
    setDoBetInCombination({ combinations: isValidResults.result, betType, betTypeChip: chip });
  };

  return {
    allChipsTotal,
    checkValidChip,
    handleDoBetWithValid,
    handleDoubleBetWithValid,
    handleDoBetInCombination,
  };
};
